import React from "react";
import {
	BlueSquares1,
	BlueSquares2,
	PinkSquares0,
	PinkSquares1,
} from "@assets/Background";
import { PageLayout } from "@components";
import { BackgroundShapes } from "@components/BackgroundShapes/BackgroundShapes";
import { GetADemo } from "@components/GetADemo/GetADemo";
import { StaticImage } from "gatsby-plugin-image";
import { SectionTitle } from "@components/SectionTitle/SectionTitle";
import { useWindowSize } from "@helpers/Hooks";
import CentralizedPlatform from "@components/CentralizedPlatform/CentralizedPlatform";
import FeatureCard, {
	FeatureCardLayout,
	FeatureCardProps,
} from "@components/FeatureCard/FeatureCard";
import { JoinInstitutionsV2 } from "../../landingpagev2/_components/_JoinInstitutionsV2/_JoinInstitutionsV2";
import CentralizedPlatformV2 from "@components/CentralizedPlatform/CentralizedPlatformV2";
import * as styles from "./Biopharmaceutical.module.scss";
import Margins from "@components/Margins/Margins";
import { Colors } from "@helpers/Colors";
import LabConsumablesCards from "pages/products/lab-consumables/_components/LabConsumablesCards";
import { CardWithIconProps } from "@components/CardWithIcon/CardWithIcon";
import classNames from "classnames";
import { LogosAndG2Section } from "@components/Solutions/LogosAndG2Section";

export default function Biopharma() {
	const { width } = useWindowSize();

	const demoProps = {
		title: "Lab management software for biopharmaceutical R&D",
		subtitle:
			"Optimize your biopharmaceutical lab’s performance with scalable lab management tools.",
		srcImg:
			width < 1280
				? "woman-checks-bottles-on-shelf-tablet.png"
				: "woman-checks-bottles-on-shelf.png",
		disableBackgroundShapes: true,
		rightSectionStylesOverride: styles.rightSectionStylesOverride,
		leftSectionOverride: styles.leftSectionOverride,
		getADemoOverride: styles.getADemoOverride,
		titleStylesOverride: styles.titleStylesOverride,
		subtitleStylesOverride: styles.subtitleStylesOverride,
	};

	const featuresContent: FeatureCardProps[] = [
		{
			key: "1",
			title: "Integrated biopharma research solutions",
			list: [
				"Genemod supports the entire lifecycle of protein therapeutics, from initial research to manufacturing, ensuring data integrity and regulatory compliance.",
				"Streamline cell line development with our Laboratory Information Management System, providing robust data management and traceability from initial screening to final selection.",
				"Accelerate the development of therapeutic antibodies with Genemod LIMS, offering robust data management and streamlined workflows for research and production.",
			],
			image: (
				<StaticImage
					src={"../../../assets/solutions/platform-dashboard.png"}
					alt="Easily Manage and Submit Orders"
				/>
			),
			orientation: "right",
			imageBottom: true,
			className: styles.featureQuickAccess,
			listItemWeight: "SEMIBOLD",
			titleStylesOverride: styles.featureTitleStylesOverride,
		},
		{
			key: "2",
			title: "Advanced tracking for sample data",
			list: [
				"Streamline your monoclonal antibody research with our lab inventory management system, ensuring precise tracking and management from development to clinical trials.",
				"Ensure seamless monoclonal antibody production, integrating advanced tracking and quality control measures.",
				"Optimize the development of drug delivery systems with our LIMS, providing precise management of formulation and testing data.",
			],
			image: (
				<StaticImage
					src={"../../../assets/solutions/box-view.png"}
					alt="MS Samples"
				/>
			),
			imageBottom: true,
			className: styles.featureQuickAccess,
			listItemWeight: "SEMIBOLD",
		},
		{
			key: "3",
			title: "Data-driven biopharma innovation",
			list: [
				"Optimize your bioassay development process with our Laboratory Information Management System, delivering accurate and reliable data management for potency and efficacy testing. ",
				"Utilize LIMS for comprehensive pharmacokinetics studies, ensuring precise analysis and reporting of drug absorption, distribution, metabolism, and excretion.",
				"Protect your biopharmaceutical innovations with our LIMS, ensuring secure and organized documentation for patent applications.",
			],
			image: (
				<StaticImage
					src={"../../../assets/solutions/notebook-file.png"}
					alt="Optimize your bioassay development process with our Laboratory Information Management System"
				/>
			),
			orientation: "right",
			imageBottom: true,
			className: styles.featureQuickAccessSpacing,
			listItemWeight: "SEMIBOLD",
		},
		{
			key: "4",
			title: "Secure data for biopharma compliance",
			list: [
				"Enhance the development and production of therapeutic proteins with Genemod’s LIMS, providing robust data management and compliance with regulatory standards",
				"Leverage Genemod LIMS for cutting-edge vaccine development, providing a secure and efficient platform for managing research data and regulatory compliance.",
				"Enhance your biopharmaceutical manufacturing processes with our LIMS, offering comprehensive support for GMP compliance and quality assurance.",
			],
			image: (
				<StaticImage
					src={"../../../assets/solutions/integration-hero.png"}
					alt="Enhance the development and production of therapeutic proteins with Genemod’s LIMS"
				/>
			),
			imageBottom: true,
			className: styles.featureQuickAccessSpacing,
			listItemWeight: "SEMIBOLD",
		},
	];

	const footerContent = {
		headerText: "Powered by Genemod",
		subheaderText:
			"Genemod enables large institutions with multiple teams to centralize projects and experiments on one platform. Google Docs isn’t built for life science R&D - so we built a platform that is.",
	};
	const labConsumablesCards: CardWithIconProps[] = [
		{
			icon: (
				<svg
					width={32}
					height={33}
					viewBox="0 0 32 33"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
				>
					<rect
						y="0.666016"
						width={32}
						height={32}
						rx={8}
						fill="#F5F7FF"
					/>
					<g clipPath="url(#clip0_898_7036)">
						<path
							d="M23 7.66602H13C11.8954 7.66602 11 8.56145 11 9.66602V19.666C11 20.7706 11.8954 21.666 13 21.666H23C24.1046 21.666 25 20.7706 25 19.666V9.66602C25 8.56145 24.1046 7.66602 23 7.66602Z"
							stroke="#2C52F7"
							strokeWidth={2}
							strokeLinecap="round"
							strokeLinejoin="round"
						/>
						<path
							d="M21 21.666V23.666C21 24.1964 20.7893 24.7052 20.4142 25.0802C20.0391 25.4553 19.5304 25.666 19 25.666H9C8.46957 25.666 7.96086 25.4553 7.58579 25.0802C7.21071 24.7052 7 24.1964 7 23.666V13.666C7 13.1356 7.21071 12.6269 7.58579 12.2518C7.96086 11.8767 8.46957 11.666 9 11.666H11"
							stroke="#2C52F7"
							strokeWidth={2}
							strokeLinecap="round"
							strokeLinejoin="round"
						/>
					</g>
					<defs>
						<clipPath id="clip0_898_7036">
							<rect
								width={24}
								height={24}
								fill="white"
								transform="translate(4 4.66602)"
							/>
						</clipPath>
					</defs>
				</svg>
			),
			title: "A source of truth for your inventory",
			description:
				"From reagents to custom item types - we customize to match exactly how it is in the lab.",
			to: "INVENTORY",
			Cta: "Virtual freezers",
		},
		{
			icon: (
				<svg
					width={32}
					height={33}
					viewBox="0 0 32 33"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
				>
					<rect
						y="0.666016"
						width={32}
						height={32}
						rx={8}
						fill="#F5F7FF"
					/>
					<g clipPath="url(#clip0_898_5927)">
						<path
							d="M16 14.666C16 13.0747 15.3679 11.5486 14.2426 10.4234C13.1174 9.29816 11.5913 8.66602 10 8.66602H7V10.666C7 12.2573 7.63214 13.7834 8.75736 14.9087C9.88258 16.0339 11.4087 16.666 13 16.666H16"
							stroke="#2C52F7"
							strokeWidth={2}
							strokeLinecap="round"
							strokeLinejoin="round"
						/>
						<path
							d="M16 18.666C16 17.0747 16.6321 15.5486 17.7574 14.4234C18.8826 13.2982 20.4087 12.666 22 12.666H25V13.666C25 15.2573 24.3679 16.7834 23.2426 17.9087C22.1174 19.0339 20.5913 19.666 19 19.666H16"
							stroke="#2C52F7"
							strokeWidth={2}
							strokeLinecap="round"
							strokeLinejoin="round"
						/>
						<path
							d="M16 24.666V14.666"
							stroke="#2C52F7"
							strokeWidth={2}
							strokeLinecap="round"
							strokeLinejoin="round"
						/>
					</g>
					<defs>
						<clipPath id="clip0_898_5927">
							<rect
								width={24}
								height={24}
								fill="white"
								transform="translate(4 4.66602)"
							/>
						</clipPath>
					</defs>
				</svg>
			),
			title: "Finally, an ELN built for scientists",
			description:
				"It’s never been easier to cycle across all your experiments, protocols, and attachments in one place.",
			to: "NOTEBOOK",
			Cta: "Explore ELN",
		},
		{
			icon: (
				<svg
					width={32}
					height={33}
					viewBox="0 0 32 33"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
				>
					<rect
						y="0.666016"
						width={32}
						height={32}
						rx={8}
						fill="#F5F7FF"
					/>
					<path
						d="M16 6.66602C16.5917 6.66602 17.1713 6.7174 17.7348 6.81595M16 6.66602C10.4772 6.66602 6 11.1432 6 16.666C6 22.1889 10.4772 26.666 16 26.666M16 6.66602V26.666M21.738 8.47502C22.6922 9.14471 23.5241 9.97691 24.1934 10.9314M25.8501 14.9316C25.9486 15.495 26 16.0745 26 16.666C26 17.2575 25.9486 17.8371 25.8501 18.4004M24.1892 22.4066C23.5203 23.359 22.6896 24.1893 21.7369 24.8578M17.7328 26.5164C17.17 26.6148 16.591 26.666 16 26.666"
						stroke="#2C52F7"
						strokeWidth="1.5"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
				</svg>
			),
			title: "Transparent and scalable pricing",
			description:
				"Finally, simple and predictable pricing that scales with your team.",
			to: "PRICING",
			Cta: "View pricing",
		},
	];

	return (
		<PageLayout
			seoOptions={{ type: "PREDEFINED", pageName: "pharmaceutical" }}
		>
			<Margins className={styles.demoMargins}>
				<Demo {...demoProps} />
				<FeatureCardLayout className={styles.featuresLayout}>
					{featuresContent?.map((feature) => {
						return <FeatureCard key={feature?.key} {...feature} />;
					})}
				</FeatureCardLayout>
			</Margins>
			<Margins className={styles.labConsumables_Container}>
				<LabConsumablesCards
					cards={labConsumablesCards}
					hideSignUpButton={true}
					sectionWrapperClassName={styles.LabSectionWrapperClassName}
				/>
			</Margins>
			<LogosAndG2Section />
			<Margins className={styles.institutionsMargins}>
				<JoinInstitutionsV2
					{...footerContent}
					outerContainerClassName={
						styles.institutionsOuterContainerClassName
					}
					useHeaderStylesV4={true}
					useSubheaderStylesV4={true}
					joinInstitutionsContainerOverride={
						styles.joinInstitutionsContainerOverride
					}
				/>
			</Margins>
		</PageLayout>
	);
}

type GetADemoProps = {
	titleText?: string;
	titleColor?: Colors;
	subtitleText?: string;
	subTitleColor?: Colors;
	srcImg?: string;
	showSignUpFreeButton?: boolean;
	altImg?: string;
	rightSectionStylesOverride?: string;
	leftSectionOverride?: string;
	getADemoOverride?: string;
	titleStylesOverride?: string;
	subtitleStylesOverride?: string;
	buttonContainerOverride?: string;
};

const GetADemoV2 = ({
	titleText,
	titleColor,
	subtitleText,
	subTitleColor,
	srcImg = "co-editing_snippet.png",
	showSignUpFreeButton = true,
	rightSectionStylesOverride,
	leftSectionOverride,
	getADemoOverride,
	titleStylesOverride,
	subtitleStylesOverride,
	buttonContainerOverride
}: GetADemoProps) => {
	const { width } = useWindowSize();

	const images: { [key: string]: JSX.Element } = {
		"woman-checks-bottles-on-shelf.png": (
			<StaticImage
				src={
					"../../../assets/products/woman-checks-bottles-on-shelf2.png"
				}
				alt="woman checks bottles on shelf"
			/>
		),
		"woman-checks-bottles-on-shelf-tablet.png": (
			<StaticImage
				src={
					"../../../assets/products/woman-checks-bottles-on-shelf-tablet.png"
				}
				alt="woman checks bottles on shelf"
				className={styles.bannerImage}
			/>
		),
		"feeding-conveyor-packaging-materials.png": (
			<StaticImage
				src={
					"../../../assets/solutions/feeding-conveyor-packaging-materials.png"
				}
				alt="feeding conveyor packaging material"
				style={{
					...(width > 1280
						? { maxWidth: "640px" }
						: { maxWidth: "100%" }),
				}}
				className={styles.bannerImage}
			/>
		),
		"lab-worker-doing-test.png": (
			<StaticImage
				src={"../../../assets/solutions/lab-worker-doing-test.png"}
				alt="Lab workers doing test"
				style={{
					...(width > 1280
						? { maxWidth: "640px" }
						: { maxWidth: "100%" }),
				}}
				className={styles.bannerImage}
			/>
		),

		"biomanufacturing-operation.png": (
			<StaticImage
				src={"../../../assets/products/biomanufacturing-operation.png"}
				alt="biomanufacturing operation"
				style={{
					...(width > 1280
						? { maxWidth: "640px" }
						: { maxWidth: "100%" }),
				}}
				className={styles.bannerImage}
			/>
		),
		"person-operates-equipment.png": (
			<StaticImage
				src={"../../../assets/products/person-operates-equipment.png"}
				alt="person operates equipment"
				style={{
					...(width > 1280
						? { maxWidth: "640px" }
						: { maxWidth: "100%" }),
				}}
				className={styles.bannerImage}
			/>
		),
		"man-do-sample-test-in-lab.png": (
			<StaticImage
				src={"../../../assets/products/man-do-sample-test-in-lab.png"}
				alt="man do sample test in lab"
				style={{
					...(width > 1280
						? { maxWidth: "640px" }
						: { maxWidth: "100%" }),
				}}
				className={styles.bannerImage}
			/>
		),
		"medical-practitioner-inside-of-a-lab.png": (
			<StaticImage
				src={
					"../../../assets/products/medical-practitioner-inside-of-a-lab.png"
				}
				alt="medical practitioner inside of a lab"
				style={{
					...(width > 1280
						? { maxWidth: "640px" }
						: { maxWidth: "100%" }),
				}}
				className={styles.bannerImage}
			/>
		),
		"person-holding-silver-black-hand-tool.png": (
			<StaticImage
				src={
					"../../../assets/products/person-holding-silver-black-hand-tool.png"
				}
				alt="person holding silver black hand tool"
				style={{
					...(width > 1280
						? { maxWidth: "640px" }
						: { maxWidth: "100%" }),
				}}
				className={styles.bannerImage}
			/>
		),
		"research-center-of-the-ecole-poly-technique.png": (
			<StaticImage
				src={
					"../../../assets/products/research-center-of-the-ecole-poly-technique.png"
				}
				alt="research center of the ecole poly technique"
				style={{
					...(width > 1280
						? { maxWidth: "640px" }
						: { maxWidth: "100%" }),
				}}
				className={styles.bannerImage}
			/>
		),
		"professional-services-by-specialists.png": (
			<StaticImage
				src={
					"../../../assets/products/professional-services-by-specialists.png"
				}
				alt="professional services by the Genemod specialists"
				style={{
					...(width > 1280
						? { maxWidth: "640px" }
						: { maxWidth: "100%" }),
				}}
				className={styles.bannerImage}
			/>
		),
		"blue-leather-boxing.png": (
			<StaticImage
				src={"../../../assets/solutions/blue-leather-boxing.png"}
				style={{
					...(width > 1280
						? { maxWidth: "720px" }
						: { maxWidth: "100%" }),
				}}
				className={styles.bannerImage}
				alt="gloves"
			/>
		),
		"behind-the-lab-work.png": (
			<StaticImage
				src={"../../../assets/products/behind-the-lab-work.png"}
				alt="behind the lab work"
				style={{
					...(width > 1280
						? { maxWidth: "640px" }
						: { maxWidth: "100%" }),
				}}
				className={styles.bannerImage}
			/>
		),
		"co-editing_snippet.png": (
			<StaticImage
				loading="eager"
				src={"../../../assets/products/co-editing_snippet.png"}
				alt="co-editing product example"
			/>
		),
	};

	return (
		<GetADemo
			titleText={
				titleText
					? titleText
					: "Accelerate your therapeutic research and development"
			}
			subtitleText={
				subtitleText
					? subtitleText
					: "Genemod’s biopharmaceutical R&D platform is trusted by hundreds of biopharmaceutical innovators to accelerate their R&D."
			}
			titleColor={ titleColor ? titleColor : "text-primary"}
			linkColor="marine"
			showSignUp={true}
			showSignUpFreeButton={showSignUpFreeButton}
			imageNode={images[srcImg]}
			subtitleWidth={656}
			titleWidth={656}
			subTitleColor={ subTitleColor ? subTitleColor : "text-helper"}
			getADemoOverride={getADemoOverride}
			leftSectionOverride={leftSectionOverride}
			rightSectionStylesOverride={rightSectionStylesOverride}
			subtitleStylesOverride={subtitleStylesOverride}
			titleStylesOverride={titleStylesOverride}
			buttonContainerOverride={buttonContainerOverride}
		/>
	);
};

type DemoProps = {
	disableBackgroundShapes?: boolean;
	title?: string;
	titleColor?: Colors;	
	subtitle?: string;
	subTitleColor?: Colors;
	srcImg?: string;
	altImg?: string;
	showSignUpFreeButton?: boolean;
	rightSectionStylesOverride?: string;
	leftSectionOverride?: string;
	getADemoOverride?: string;
	titleStylesOverride?: string;
	subtitleStylesOverride?: string;
	buttonContainerOverride?: string;
};
export const Demo = ({
	disableBackgroundShapes,
	title,
	titleColor,
	subtitle,
	subTitleColor,
	srcImg,
	altImg,
	showSignUpFreeButton,
	rightSectionStylesOverride,
	leftSectionOverride,
	getADemoOverride,
	titleStylesOverride,
	subtitleStylesOverride,
	buttonContainerOverride
}: DemoProps) => (
	<div>
		{disableBackgroundShapes ? (
			<GetADemoV2
				titleText={title}
				titleColor={titleColor}
				subtitleText={subtitle}
				subTitleColor={subTitleColor}
				srcImg={srcImg}
				altImg={altImg}
				showSignUpFreeButton={showSignUpFreeButton}
				rightSectionStylesOverride={rightSectionStylesOverride}
				leftSectionOverride={leftSectionOverride}
				getADemoOverride={getADemoOverride}
				subtitleStylesOverride={subtitleStylesOverride}
				titleStylesOverride={titleStylesOverride}
				buttonContainerOverride={buttonContainerOverride}
			/>
		) : (
			<BackgroundShapes
				leftShape={{
					shape: <BlueSquares1 />,
					xOffset: -140,
					yOffset: 120,
				}}
				rightShape={{ shape: <BlueSquares2 />, xOffset: 140 }}
			>
				<GetADemoV2
					titleText={title}
					subtitleText={subtitle}
					srcImg={srcImg}
					altImg={altImg}
				/>
			</BackgroundShapes>
		)}
	</div>
);

type GetWorkDoneProps = {
	title?: string;
	subtitle?: string;
	skewTop?: boolean;
	skewBottom?: boolean;
	showSquares?: boolean;
	showCentralizedPlatformV2?: boolean;
	showSubtitleBottom?: boolean;
	widthOnLargeScreensClass?: string;
	titleWidth?: number;
	subtitleWidth?: number;
	applyV4Styles?: boolean;
	sectionWrapper?: string;
	image?: React.ReactNode;
};

export const GetWorkDone = ({
	title,
	subtitle,
	showSquares,
	showCentralizedPlatformV2,
	showSubtitleBottom,
	applyV4Styles,
	titleWidth = 800,
	subtitleWidth = 800,
	sectionWrapper = "",
	image,
}: GetWorkDoneProps) => {
	return (
		<div
			className={classNames(
				styles.getWorkDoneWidthOnLargeScreensClass,
				sectionWrapper
			)}
		>
			{showSquares ? (
				<BackgroundShapes
					leftShape={{
						shape: <PinkSquares0 />,
						xOffset: -140,
						yOffset: 10,
					}}
					rightShape={{
						shape: <PinkSquares1 />,
						xOffset: 170,
						yOffset: 180,
					}}
				>
					<SectionTitle
						title={title ? title : ""}
						subtitle={
							subtitle && !showSubtitleBottom ? subtitle : ""
						}
						textColor="text-dark"
						titleWidth={1088}
						subtitleWidth={596}
						marginBottom={24}
						marginBottomTablet={24}
						marginBottomMobile={24}
					/>
					{showCentralizedPlatformV2
						? image ?? <CentralizedPlatformV2 light />
						: image ?? <CentralizedPlatform light />}
				</BackgroundShapes>
			) : (
				<>
					<SectionTitle
						title={title ? title : ""}
						subtitle={
							subtitle && !showSubtitleBottom ? subtitle : ""
						}
						textColor="text-primary"
						subtitleTextColor="text-helper"
						titleWidth={titleWidth}
						subtitleWidth={subtitleWidth}
						marginTop={0}
						marginBottom={0}
						marginBottomTablet={0}
						marginTopTablet={0}
						marginTopMobile={0}
						marginBottomMobile={0}
						applyV4Styles={applyV4Styles}
					/>
					{showCentralizedPlatformV2
						? image ?? (
								<CentralizedPlatformV2
									light
									style={{ width: "100%" }}
								/>
						  )
						: image ?? <CentralizedPlatform light />}
					{subtitle && showSubtitleBottom && (
						<SectionTitle
							title={""}
							subtitle={
								subtitle && showSubtitleBottom ? subtitle : ""
							}
							subtitleOverride={styles.subtitleOverride}
							textColor="text-helper"
							titleWidth={1088}
							marginTopTablet={0}
							marginTopMobile={0}
							marginBottomMobile={0}
							subtitleWidth={subtitleWidth??1040}
							marginBottom={0}
							marginTop={0}
							marginBottomTablet={0}
						/>
					)}
				</>
			)}
		</div>
	);
};
